import { apiClient, API_PRX, useAuthStore } from '@indipia/shared';
import type { ApiResponse } from '@indipia/shared-types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { CompanyMember } from './types';

/** GET /member/company/members — 사업장 구성원(사장님 + ACTIVE 직원). 사장님 먼저, 이름순 */
export async function fetchCompanyMembers(): Promise<CompanyMember[]> {
  const res = await apiClient.get<ApiResponse<CompanyMember[]>>(`${API_PRX.MEMBER}/company/members`);
  return (res.data.data ?? []) as CompanyMember[];
}

/**
 * 구성원은 자주 바뀌지 않으므로 화면 진입 시 한 번 조회해 재사용한다
 * (같은 화면의 필터 · 현장 등록 팝업 · 상세 편집이 같은 목록을 쓴다 — D.5.3).
 * 계정을 바꿨을 때 이전 사업장 목록이 보이지 않게 compId 를 키에 넣는다.
 */
export function useCompanyMembers() {
  const compId = useAuthStore((s) => s.user?.compId ?? null);
  return useQuery({
    queryKey: ['company-members', compId],
    queryFn: fetchCompanyMembers,
    enabled: compId != null,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * 로그인 사용자 기준 담당자·관리자 기본 조회 조건(D.0) — 직원은 나, 사장님은 조건 없음(전체 현장).
 */
export function useDefaultMemberFilter(): { myId: number | null; isOwner: boolean; defaultIds: number[] } {
  const user = useAuthStore((s) => s.user);
  const myId = user?.memberId ?? null;
  const isOwner = user?.masterYn === 'Y';
  return { myId, isOwner, defaultIds: !isOwner && myId != null ? [myId] : [] };
}

/**
 * 기본값이 로그인 사용자에 따라 정해지는 상태 — 로그인 정보가 마운트 뒤에 들어오거나 계정이 바뀌면
 * 그 사용자 기준 기본값으로 다시 맞춘다(첫 렌더의 useState 초기값이 빈 값으로 굳는 문제 방지).
 * 같은 사용자인 동안에는 사용자가 바꾼 값을 그대로 둔다.
 */
function useUserDefaultedState<T>(userKey: string, initial: T): [T, (v: T) => void] {
  const [state, setState] = useState<{ key: string; value: T }>({ key: userKey, value: initial });
  if (state.key !== userKey) {
    // 렌더 중 상태 보정(React 권장 패턴) — 다음 렌더부터 새 기본값
    setState({ key: userKey, value: initial });
    return [initial, (v: T) => setState({ key: userKey, value: v })];
  }
  return [state.value, (v: T) => setState({ key: userKey, value: v })];
}

/** 담당자·관리자 조회 조건 상태 — 기본값: 직원은 [나], 사장님은 [](전체) */
export function useMemberFilterState(): [number[], (ids: number[]) => void] {
  const { myId, isOwner, defaultIds } = useDefaultMemberFilter();
  return useUserDefaultedState(`${myId ?? ''}|${isOwner}`, defaultIds);
}

/** '☑ 내 현장만' 상태 — 기본값: 직원은 체크, 사장님은 해제 */
export function useMineOnlyState(): [boolean, (v: boolean) => void] {
  const { myId, isOwner } = useDefaultMemberFilter();
  return useUserDefaultedState(`${myId ?? ''}|${isOwner}`, myId != null && !isOwner);
}

/** 로그인 사용자가 정해지면 다시 맞추는 일반 상태(예: 현장 등록 팝업의 담당자 기본값 = 본인) */
export { useUserDefaultedState };
